import { useState, useRef } from 'react';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { CurrencyInput } from '@/components/ui/currency-input';
import { SubmitDealStepProps } from '@/types/submit-deal.types';
import type { InventoryItem } from '@/types/submit-deal.types';
import { useLanguage } from '@/contexts/LanguageContext';
import { useToast } from '@/hooks/use-toast';
import { uploadAnalyzedDocuments, getOrCreateDraftSessionId } from '@/lib/upload-deal-documents';
import { postApi } from '@/lib/api';
import { Plus, Trash2, Upload, Sparkles, Loader2, FileText, Wrench } from 'lucide-react';

function fmt(n: number, currency = 'CAD') {
  return new Intl.NumberFormat('fr-CA', { style: 'currency', currency, minimumFractionDigits: 0 }).format(n || 0);
}

export const StepAssets = ({ formData, updateFormData, errors, clearError, t }: SubmitDealStepProps) => {
  const { language } = useLanguage();
  const { toast } = useToast();
  const isFr = language === 'fr';
  const currency = formData.general?.currency || 'CAD';
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [uploading, setUploading] = useState(false);
  const [extracting, setExtracting] = useState<string | null>(null); // path du fichier en cours d'extraction

  const handleChange = (field: string, value: string | number | boolean) => {
    updateFormData('assets', { [field]: value });
    clearError(field);
  };

  const handleNumericChange = (field: string, rawValue: string) => {
    const value = rawValue === '' ? '' : parseInt(rawValue);
    updateFormData('assets', { [field]: value });
    clearError(field);
  };

  // ── Répéteur manuel — inventoryItems[] (type déjà défini, jamais utilisé) ──
  const items: InventoryItem[] = formData.assets.inventoryItems || [];

  const addItem = () => {
    const newItem: InventoryItem = {
      id: `item_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      description: '',
      category: '',
      quantity: 1,
      unitValue: 0,
    };
    updateFormData('assets', { inventoryItems: [...items, newItem] });
  };

  const updateItem = (id: string, field: keyof InventoryItem, value: string | number) => {
    updateFormData('assets', {
      inventoryItems: items.map(it => (it.id === id ? { ...it, [field]: value } : it)),
    });
  };

  const removeItem = (id: string) => {
    updateFormData('assets', { inventoryItems: items.filter(it => it.id !== id) });
  };

  const itemsTotal = items.reduce((sum, it) => sum + (Number(it.quantity) || 0) * (Number(it.unitValue) || 0), 0);

  // ── Upload de documents (registre d'actifs existant) ────────────────────
  const documents = formData.assets.assetListDocuments || [];

  const handleUpload = async (files: FileList) => {
    setUploading(true);
    try {
      const sessionId = getOrCreateDraftSessionId();
      const fileList = Array.from(files);
      const base64Files = await Promise.all(
        fileList.map(f => new Promise<{ name: string; base64: string; mimeType: string; size: number }>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const result = reader.result as string;
            resolve({ name: f.name, base64: result.split(',')[1], mimeType: f.type, size: f.size });
          };
          reader.onerror = reject;
          reader.readAsDataURL(f);
        }))
      );
      const uploaded = await uploadAnalyzedDocuments(
        sessionId,
        base64Files.map(({ name, base64, mimeType }) => ({ name, base64, mimeType }))
      );
      const newDocs = uploaded.map((doc, i) => ({
        name: doc.name,
        size: base64Files[i].size,
        type: doc.mimeType,
        url: doc.url,
        path: doc.path,
      }));
      updateFormData('assets', { assetListDocuments: [...documents, ...newDocs] });
      toast({ description: isFr ? 'Document ajouté' : 'Document added' });
    } catch (err) {
      console.error('[StepAssets] upload failed:', err);
      toast({
        title: isFr ? 'Erreur de téléversement' : 'Upload error',
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
    }
  };

  const removeDocument = (path: string) => {
    updateFormData('assets', { assetListDocuments: documents.filter(d => d.path !== path) });
  };

  // ── Extraction IA — jumelle de FinancialDocumentAnalyzer, ciblée sur
  // l'inventaire d'équipement plutôt que les états financiers. Lit le
  // fichier déjà uploadé (via son URL Storage) et demande au backend de
  // structurer son contenu en lignes d'inventaire, fusionnées (pas
  // remplacées) dans le répéteur manuel ci-dessus.
  const extractWithAI = async (doc: { name: string; url: string; path: string; type: string }) => {
    setExtracting(doc.path);
    try {
      const fileRes = await fetch(doc.url);
      const blob = await fileRes.blob();
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve((reader.result as string).split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

      const result = await postApi('/ai/analyze-asset-list', {
        documents: [{ name: doc.name, base64, mimeType: doc.type }],
      });

      const extractedItems: Partial<InventoryItem>[] = result?.items || [];
      if (extractedItems.length === 0) {
        toast({
          description: isFr
            ? "Aucun élément d'inventaire détecté dans ce document."
            : 'No inventory items detected in this document.',
        });
        return;
      }

      const merged: InventoryItem[] = [
        ...items,
        ...extractedItems.map((it, i) => ({
          id: `item_ai_${Date.now()}_${i}`,
          description: it.description || '',
          category: it.category || '',
          quantity: it.quantity || 1,
          unitValue: it.unitValue || 0,
        })),
      ];
      updateFormData('assets', { inventoryItems: merged });
      toast({
        description: isFr
          ? `${extractedItems.length} élément(s) ajouté(s) à la liste ci-dessous — vérifiez et ajustez au besoin.`
          : `${extractedItems.length} item(s) added below — review and adjust as needed.`,
      });
    } catch (err) {
      console.error('[StepAssets] AI extraction failed:', err);
      toast({
        title: isFr ? "Erreur d'extraction" : 'Extraction error',
        description: isFr
          ? 'Impossible d\'analyser ce document automatiquement — vous pouvez toujours saisir les éléments manuellement.'
          : 'Could not analyze this document automatically — you can still enter items manually.',
        variant: 'destructive',
      });
    } finally {
      setExtracting(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="inventoryValue">{t.step4.inventoryValue} *</Label>
          <CurrencyInput
            id="inventoryValue"
            min={0}
            value={formData.assets.inventoryValue || ''}
            onChange={(v) => handleNumericChange('inventoryValue', v)}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="ffeValue">{t.step4.ffeValue} *</Label>
          <CurrencyInput
            id="ffeValue"
            min={0}
            value={formData.assets.ffeValue || ''}
            onChange={(v) => handleNumericChange('ffeValue', v)}
          />
          <p className="text-xs text-muted-foreground">{t.step4.ffeValueHint}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="accountsReceivable">{t.step4.accountsReceivable} *</Label>
          <CurrencyInput
            id="accountsReceivable"
            min={0}
            value={formData.assets.accountsReceivable || ''}
            onChange={(v) => handleNumericChange('accountsReceivable', v)}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="otherCurrentAssets">{t.step4.otherCurrentAssets} *</Label>
          <CurrencyInput
            id="otherCurrentAssets"
            min={0}
            value={formData.assets.otherCurrentAssets || ''}
            onChange={(v) => handleNumericChange('otherCurrentAssets', v)}
          />
        </div>
      </div>

      {/* AJOUTÉ : Inventaire détaillé — équipement, machinerie, véhicules.
          Deux façons de le remplir, qui coexistent : saisie manuelle ligne
          par ligne, et/ou upload d'un registre existant avec extraction IA. */}
      <div className="border-t pt-6 space-y-4">
        <div>
          <h4 className="text-lg font-medium flex items-center gap-2">
            <Wrench className="h-4 w-4" />
            {isFr ? 'Inventaire détaillé (équipement, machinerie, véhicules)' : 'Detailed Inventory (Equipment, Machinery, Vehicles)'}
          </h4>
          <p className="text-xs text-muted-foreground mt-1">
            {isFr
              ? "Optionnel, mais recommandé pour la crédibilité auprès des acheteurs. Ajoutez manuellement et/ou téléversez un registre existant."
              : 'Optional but recommended for buyer credibility. Add manually and/or upload an existing registry.'}
          </p>
        </div>

        {/* Upload de document */}
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.xlsx,.xls,.csv"
            multiple
            className="hidden"
            onChange={(e) => e.target.files && handleUpload(e.target.files)}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
            {isFr ? 'Téléverser un registre d\'actifs' : 'Upload an Asset Registry'}
          </Button>

          {documents.length > 0 && (
            <div className="mt-3 space-y-2">
              {documents.map((doc) => (
                <div key={doc.path} className="flex items-center justify-between p-2 rounded-md border border-border bg-muted/20">
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                    <a href={doc.url} target="_blank" rel="noreferrer" className="text-sm truncate hover:underline">
                      {doc.name}
                    </a>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => extractWithAI(doc)}
                      disabled={extracting === doc.path}
                    >
                      {extracting === doc.path ? (
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      ) : (
                        <Sparkles className="h-4 w-4 mr-1" />
                      )}
                      {isFr ? 'Extraire avec IA' : 'Extract with AI'}
                    </Button>
                    <Button type="button" size="icon" variant="ghost" className="text-destructive h-8 w-8" onClick={() => removeDocument(doc.path)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Répéteur manuel */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-sm">{isFr ? 'Liste des éléments' : 'Item List'} ({items.length})</Label>
            <Button type="button" size="sm" variant="outline" onClick={addItem}>
              <Plus className="h-4 w-4 mr-1" />
              {isFr ? 'Ajouter un élément' : 'Add Item'}
            </Button>
          </div>

          {items.length === 0 ? (
            <p className="text-sm text-muted-foreground italic p-3 border border-dashed rounded-md text-center">
              {isFr ? 'Aucun élément ajouté pour le moment.' : 'No items added yet.'}
            </p>
          ) : (
            <div className="space-y-2">
              {items.map((item) => (
                <div key={item.id} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end p-2 rounded-md bg-muted/20 border border-border">
                  <div className="md:col-span-5 space-y-1">
                    <Label className="text-xs">{isFr ? 'Description' : 'Description'}</Label>
                    <Input
                      value={item.description}
                      onChange={(e) => updateItem(item.id, 'description', e.target.value)}
                      placeholder={isFr ? 'ex. Excavateur Komatsu PC200-10' : 'e.g. Komatsu PC200-10 Excavator'}
                    />
                  </div>
                  <div className="md:col-span-3 space-y-1">
                    <Label className="text-xs">{isFr ? 'Catégorie' : 'Category'}</Label>
                    <Input
                      value={item.category}
                      onChange={(e) => updateItem(item.id, 'category', e.target.value)}
                      placeholder={isFr ? 'Équipement lourd' : 'Heavy Equipment'}
                    />
                  </div>
                  <div className="md:col-span-1 space-y-1">
                    <Label className="text-xs">{isFr ? 'Qté' : 'Qty'}</Label>
                    <Input
                      type="number"
                      min={1}
                      value={item.quantity || ''}
                      onChange={(e) => updateItem(item.id, 'quantity', parseInt(e.target.value) || 1)}
                    />
                  </div>
                  <div className="md:col-span-2 space-y-1">
                    <Label className="text-xs">{isFr ? 'Valeur unitaire' : 'Unit Value'}</Label>
                    <CurrencyInput
                      min={0}
                      value={item.unitValue || ''}
                      onChange={(v) => updateItem(item.id, 'unitValue', v === '' ? 0 : parseFloat(v))}
                    />
                  </div>
                  <div className="md:col-span-1 flex justify-end">
                    <Button type="button" size="icon" variant="ghost" className="text-destructive" onClick={() => removeItem(item.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
              <div className="flex justify-end pt-2 border-t border-border">
                <span className="text-sm font-semibold">
                  {isFr ? 'Total inventaire:' : 'Inventory Total:'} {fmt(itemsTotal, currency)}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="space-y-4">
        <Label>{t.step4.patentsOrTrademarks} *</Label>
        <RadioGroup
          value={formData.assets.patentsOrTrademarks ? 'yes' : 'no'}
          onValueChange={(value) => handleChange('patentsOrTrademarks', value === 'yes')}
          className="flex space-x-6"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="yes" id="patentsYes" />
            <Label htmlFor="patentsYes" className="font-normal">{t.step3.yes}</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="no" id="patentsNo" />
            <Label htmlFor="patentsNo" className="font-normal">{t.step3.no}</Label>
          </div>
        </RadioGroup>
      </div>

      {formData.assets.patentsOrTrademarks && (
        <div className="space-y-2">
          <Label htmlFor="intellectualPropertyDetails">{t.step4.intellectualPropertyDetails}</Label>
          <Textarea
            id="intellectualPropertyDetails"
            value={formData.assets.intellectualPropertyDetails}
            onChange={(e) => handleChange('intellectualPropertyDetails', e.target.value)}
            placeholder={t.step4.intellectualPropertyPlaceholder}
            rows={3}
          />
        </div>
      )}
    </div>
  );
};